/**
 * Context store — manages the dynamic variables the host site passes to
 * the agent.
 *
 * Sources, in priority order (later overrides earlier):
 *   1. `data-var-*` attributes on `<deepagent-agent>`
 *      e.g. `data-var-user-name="Mario"` -> { user_name: "Mario" }
 *      (legacy camelCase keys still work when explicitly allowed)
 *   2. The `data-context` JSON attribute
 *      e.g. `data-context='{"userName":"Mario"}'`
 *   3. The imperative JS API `widget.setContext({ ... })`
 *   4. Per-conversation `startConversation({ dynamicVariables: { ... } })`
 *
 * The store also filters against the `allowedDynamicVars` list returned by
 * the server. Unknown keys never leave snapshot().
 *
 * Server-side sanitization happens again on /api/embed/session/start, so
 * this is defense-in-depth rather than the primary guard.
 */

// Bundle the canonical pure source: no shared-types dist/runtime dependency.
import {
  EmbedApiError,
  assertEmbedVariableRecord,
  validateEmbedVariables,
} from "../../../shared-types/src/utils/embed-dynamic-variables.js";

const ATTR_PREFIX = "data-var-";

export class ContextStore {
  private values: Record<string, unknown> = {};
  private legacyValues: Record<string, string> = {};
  private attributes: Record<string, string> = {};
  private attributeContext: Record<string, unknown> = {};
  private allowed: Set<string> = new Set();
  private attributeError: EmbedApiError | null = null;

  setAllowedKeys(keys: readonly string[] | undefined): void {
    this.allowed = new Set(keys ?? []);
    // Keep pending input: config arrives asynchronously and may be refreshed.
    // snapshot() is the only exit and always enforces the current allowlist.
  }

  /**
   * Hydrate from the host element's attributes. Idempotent — safe to call on
   * every attribute change.
   */
  hydrateFromElement(element: HTMLElement): void {
    this.attributeError = null;
    let collected: Record<string, unknown> = {};
    this.attributes = {};

    for (const attr of element.attributes) {
      const name = attr.name.toLowerCase();
      if (!name.startsWith(ATTR_PREFIX)) continue;
      const key = name.slice(ATTR_PREFIX.length);
      if (!key) continue;
      this.attributes[key] = attr.value;
    }

    const json = element.getAttribute("data-context");
    if (json) {
      try {
        const parsed: unknown = JSON.parse(json);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          this.attributeError = new EmbedApiError("invalid_dynamic_variables", {
            invalidKeys: ["data-context"],
          });
          this.attributeContext = {};
          return;
        }
        collected = parsed as Record<string, unknown>;
      } catch (error) {
        this.attributeError =
          error instanceof EmbedApiError
            ? error
            : new EmbedApiError("invalid_dynamic_variables", {
                invalidKeys: ["data-context"],
              });
      }
    }

    this.attributeContext = collected;
  }

  /** Public imperative API exposed on the custom element. */
  setContext(input: Record<string, unknown>): void {
    assertEmbedVariableRecord(input);
    // Explicit undefined removes a stale key, so a caller can correct a typo.
    for (const [key, value] of Object.entries(input))
      if (value === undefined) {
        delete this.values[key];
        delete this.legacyValues[key];
      } else this.values[key] = value;
    const coerced: Record<string, string> = {};
    for (const [key, value] of Object.entries(input)) {
      if (typeof value === "string") coerced[key] = value;
      else if (typeof value === "number" || typeof value === "boolean") {
        coerced[key] = String(value);
      }
    }
    // Preserve legacy setContext: invalid values are ignored, never thrown or
    // allowed to overwrite a prior valid value. Strict starts inspect raw input.
    this.legacyValues = { ...this.legacyValues, ...coerced };
  }

  /** Returns a snapshot honoring the allowlist. */
  snapshot(overrides: Record<string, unknown> = {}): Record<string, string> {
    const out: Record<string, string> = {};
    const merged = this.rawSnapshot(overrides, false);
    for (const [key, value] of Object.entries(merged)) {
      if (
        typeof value !== "string" &&
        typeof value !== "number" &&
        typeof value !== "boolean"
      ) {
        delete out[key];
        continue;
      }
      if (this.allowed.has(key)) out[key] = String(value);
    }
    return out;
  }

  /** Strict start API: report bad input instead of silently filtering it. */
  validatedSnapshot(
    requiredKeys: readonly string[],
    overrides: Record<string, unknown> = {},
  ): Record<string, string> {
    if (this.attributeError) throw this.attributeError;
    assertEmbedVariableRecord(overrides);
    return validateEmbedVariables(
      this.rawSnapshot(overrides),
      [...this.allowed],
      requiredKeys,
    );
  }

  private rawSnapshot(
    overrides: Record<string, unknown>,
    strict = true,
  ): Record<string, unknown> {
    // Resolve names after config: exact placeholders precede legacy aliases.
    const attributes = Object.fromEntries(
      Object.entries(this.attributes).map(([attribute, value]) => {
        const key =
          [
            attribute,
            attribute.replace(/-/g, "_"),
            kebabToCamel(attribute),
          ].find((candidate) => this.allowed.has(candidate)) ?? attribute;
        return [key, value];
      }),
    );
    return {
      ...attributes,
      ...(strict
        ? this.attributeContext
        : Object.fromEntries(
            Object.entries(this.attributeContext).filter(
              ([, value]) =>
                typeof value === "string" ||
                typeof value === "number" ||
                typeof value === "boolean",
            ),
          )),
      ...(strict ? this.values : this.legacyValues),
      ...overrides,
    };
  }
}

function kebabToCamel(input: string): string {
  return input.replace(/-([a-z0-9])/gi, (_, ch: string) => ch.toUpperCase());
}
